import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CleanupInfoPanel from '../Cleanup/CleanupInfoPanel';
import { useCleanupContext } from '../Cleanup/CleanupLayout';
import dayjs from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const formatPrice = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '–';
  }
  const numeric = Number(value);
  return `${numeric.toFixed(2).replace('.', ',')} €`;
};

const formatTimeRange = (start, end) => {
  if (!start) return '–';
  const startText = dayjs(start).format('HH:mm');
  const endText = end ? dayjs(end).format('HH:mm') : null;
  return endText ? `${startText} – ${endText}` : startText;
};

export default function TemporaryReservationDayContent({
  reservations,
  reservationActionState,
  onInvalidate,
  fetchReservations,
}) {
  const { selectedEntertainmentId, selectedDate } = useCleanupContext();
  const hasSelection = Boolean(selectedEntertainmentId && selectedDate);
  const hasReservations = reservations.length > 0;
  const [isLoading, setIsLoading] = useState(false);

  const fetchReservationsRef = useRef(fetchReservations);

  useEffect(() => {
    fetchReservationsRef.current = fetchReservations;
  }, [fetchReservations]);

  const loadReservations = useCallback(async () => {
    if (!hasSelection) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      await fetchReservationsRef.current({
        entertainmentId: selectedEntertainmentId,
        date: selectedDate,
      });
    } finally {
      setIsLoading(false);
    }
  }, [hasSelection, selectedEntertainmentId, selectedDate]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const handleRefresh = useCallback(() => {
    loadReservations().catch(() => {});
  }, [loadReservations]);

  const tableContent = useMemo(() => {
    if (!hasSelection || !hasReservations) {
      return null;
    }

    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Zeit</TableCell>
            <TableCell>Produkt</TableCell>
            <TableCell align='right'>Gesamtpreis</TableCell>
            <TableCell align='right'>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reservations.map(reservation => (
            <TableRow key={reservation.id} hover>
              <TableCell>{formatTimeRange(reservation.startTime, reservation.endTime)}</TableCell>
              <TableCell>
                <Typography variant='body2' fontWeight={500}>
                  {reservation.product?.label ||
                    reservation.product?.name ||
                    reservation.product?.internalName ||
                    'Unbekanntes Produkt'}
                </Typography>
                {reservation.participantCount ? (
                  <Typography variant='caption' color='text.secondary'>
                    {reservation.participantCount} Personen
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align='right'>{formatPrice(reservation.totalPrice)}</TableCell>
              <TableCell align='right'>
                <Button
                  size='small'
                  color='error'
                  variant='contained'
                  onClick={() =>
                    onInvalidate(reservation.id, {
                      entertainmentId: selectedEntertainmentId,
                      date: selectedDate,
                    })
                  }
                  disabled={Boolean(reservationActionState[reservation.id])}
                >
                  {reservationActionState[reservation.id] ? 'Wird entfernt…' : 'Ungültig machen'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }, [
    hasSelection,
    hasReservations,
    reservations,
    reservationActionState,
    onInvalidate,
    selectedEntertainmentId,
    selectedDate,
  ]);

  if (!hasSelection) {
    return null;
  }

  const refreshAction = (
    <Button
      variant='outlined'
      startIcon={isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
      onClick={handleRefresh}
      disabled={isLoading}
    >
      {isLoading ? 'Aktualisiere…' : 'Aktualisieren'}
    </Button>
  );

  if (!hasReservations) {
    return (
      <Box sx={viewRootTextPrimarySx}>
        <CleanupInfoPanel title='Reservierungen' action={refreshAction}>
          {isLoading ? (
            <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Keine Reservierungen für diesen Tag.
            </Typography>
          )}
        </CleanupInfoPanel>
      </Box>
    );
  }

  return (
    <Box sx={viewRootTextPrimarySx}>
      <CleanupInfoPanel title='Reservierungen' action={refreshAction}>
        {isLoading ? (
          <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={36} />
          </Box>
        ) : null}
        {tableContent}
      </CleanupInfoPanel>
    </Box>
  );
}

